/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
/** tempexp_14729-full-file */
import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type FC, type ReactElement, Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { animateNewTableGrid, hideDateInNewTableGrid } from 'reactApp/appHelpers/featuresHelpers/features/datalistNewTableGrid';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadCounter from 'reactApp/components/DownloadCounter/DownloadCounter';
import { getAllDocumentsCategory } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { AllDocumentsCategoryType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getFirstPublicWithDownloadLimits, getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { getSearchContentStatus } from 'reactApp/modules/search/search.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { STORAGE_CONFIG } from 'reactApp/modules/storage/storage.config';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { Checkbox } from 'reactApp/ui/DataListItemCommonComponents/Checkbox/Checkbox';
import { Destination, SizeType } from 'reactApp/ui/DataListItemCommonComponents/Destination/Destination';
import { Download } from 'reactApp/ui/DataListItemCommonComponents/Download/Download';
import { Favorite } from 'reactApp/ui/DataListItemCommonComponents/Favorite/Favorite';
import { Progress } from 'reactApp/ui/DataListItemCommonComponents/Progress/Progress';
import { Publish } from 'reactApp/ui/DataListItemCommonComponents/Publish/Publish';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { Hint, HintPosition } from 'reactApp/ui/Hint/Hint';
import { TrackDownloadCountPromo } from 'reactApp/ui/SharingTooltips/TrackDownloadCountPromo';
import { ESearchOptionSource } from 'reactApp/ui/WebSearch/WebSearch.data';
import { WebSearchSource } from 'reactApp/ui/WebSearch/WebSearchSource/WebSearchSource';
import { noop } from 'reactApp/utils/helpers';

import type { DataListItemThumbProps as DataListItemThumbBaseProps } from '../DataListItemThumb.types';
import { Date } from './components/Date/Date';
import { ItemName } from './components/ItemName/ItemName';
import { Pic } from './components/Pic/Pic';
import styles from './DataListItemThumb.css';

type DataListItemThumbProps = DataListItemThumbBaseProps & {
    className?: string;
    isIncomingPublicOrShared: boolean;
    onAuthorContextMenuItem: (event: React.MouseEvent) => void;
};

function isPublishAvailable(storage: EStorageType, currentCategory: AllDocumentsCategoryType) {
    /**
     * Добавляем проверку на currentCategory для документов,
     * потому что на doc.mail.ru в любом разделе storage === EStorageType.alldocuments
     */
    if (currentCategory === AllDocumentsCategoryType.sharedWithYou) {
        return false;
    }

    return [
        EStorageType.home,
        EStorageType.sharedLinks,
        EStorageType.sharedAutodelete,
        EStorageType.favorites,
        EStorageType.search,
        EStorageType.feed,
        EStorageType.documents,
        EStorageType.gallery,
        EStorageType.alldocuments,
    ].includes(storage);
}

export const DataListItemThumb: FC<DataListItemThumbProps> = memo((props) => {
    const {
        author,
        ownerName,
        className,
        denyDoAction,
        name,
        ext,
        mtime,
        isPopup,
        storage,
        isSelected,
        onCheckboxClick,
        onDownload,
        isFolder,
        size,
        id,
        forceCheckbox,
        isPublicDownloaded,
        isActive,
        tipData,
        parentItemRef,
        showPublishButton,
        showDownloadButton,
        isIncomingPublicOrShared,
        onAuthorContextMenuItem,
        weblink,
    } = props;

    const config = STORAGE_CONFIG[storage];
    const { webSearchContentEnabled } = useSelector(getSearchContentStatus);

    const [pictureLoaded, setPictureLoaded] = useState<boolean>(false);
    const handlePictureLoad = () => {
        setPictureLoaded(true);
    };
    const currentCategory = useSelector(getAllDocumentsCategory);
    const isFavoritesEnable = config.favorites && 'favoritesAvailable' in props && props.favoritesAvailable;
    const isRenameAvailable = 'renameAvailable' in props && props.renameAvailable;
    const isInFavorites = 'isInFavorites' in props && props.isInFavorites;
    const showPublicButton = storage === EStorageType.home && 'showPublicButton' in props && props.showPublicButton;
    const onFavorites = 'onFavorites' in props && props.onFavorites;
    const onPublish = 'onPublish' in props && props.onPublish;
    const onCancelUpload = 'onCancelUpload' in props && props.onCancelUpload;
    const showDetails = !denyDoAction && !isPopup;
    const showWebSearchSource =
        showDetails && webSearchContentEnabled && 'srchSrc' in props && props.srchSrc === ESearchOptionSource.content && props.query;
    const showDate = showDetails && !hideDateInNewTableGrid;
    const isPublishEnable = showPublishButton && onPublish && isPublishAvailable(storage, currentCategory);
    const isAttaches = storage === EStorageType.attaches;

    const showDestination = isAttaches || isIncomingPublicOrShared;

    // tempexp_17847-start
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [showTooltipCounter, setShowTooltipCounter] = useState(false);
    const TOOLTIP_ID = 'tooltipTrackDownload';
    const isTooltipShown = Boolean(storeHelper.getValue(TOOLTIP_ID));
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const routeId = useSelector(getCurrentRouteId);
    const weblinkUrl = getWeblinkFromPublicId(routeId);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || (weblink as string) || ''));
    const downloadTotal = downloadLimits && downloadLimits.total;
    const downloadLeft = downloadLimits && downloadLimits.left;
    const downloads = downloadTotal - downloadLeft;
    const showDownloadCounter =
        enabledPublicDownloadLimits &&
        (storage === EStorageType.home || storage === EStorageType.sharedLinks) &&
        !isPaidUser &&
        downloads > 0;

    const firstPublicWithDownloads = useSelector((state: RootState) => getFirstPublicWithDownloadLimits(state));
    const weblinkForPromo = firstPublicWithDownloads && Object.keys(firstPublicWithDownloads)[0];
    const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;
    const showFirstTrackDownloadCountPromo = showTrackDownloadCountPromo && weblink === weblinkForPromo;

    const handleCounterClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltipCounter(true);
    }, []);

    const handleCounterPromoHide = useCallback(() => {
        setShowTooltipCounter(false);
    }, []);
    // tempexp_17847-end

    const progress = props.progress;

    const bottomActions = useMemo(() => {
        const content: ReactElement[] = [];

        if (storage === EStorageType.home && denyDoAction && typeof progress === 'number') {
            content.push(<Progress key={0} onClick={onCancelUpload || noop} progress={progress} />);
        } else if (!denyDoAction) {
            let key = 0;

            if (isPublishEnable) {
                content.push(
                    <Publish
                        itemId={id}
                        className={classNames(styles.publish, {
                            [styles.publish_active]: props.isPublic,
                            [styles.publish_visible]: showPublicButton && !isInFavorites,
                            [styles.publish_counter]: showDownloadCounter,
                        })}
                        key={key}
                        animate={animateNewTableGrid}
                        viewType={EViewMode.thumbs}
                        isShared={props.isPublic}
                        onClick={onPublish || noop}
                        isSeoEnabled={props.isSeoEnabled}
                        size={16}
                    />
                );

                key++;
            }

            if (isFavoritesEnable) {
                content.push(
                    <Favorite
                        key={key}
                        className={classNames(styles.favorite, {
                            [styles.favorite_active]: Boolean(isInFavorites),
                            [styles.favorite_counter]: showDownloadCounter,
                        })}
                        animate={animateNewTableGrid}
                        viewType={EViewMode.thumbs}
                        isFavorite={Boolean(isInFavorites)}
                        onClick={onFavorites || noop}
                        size={16}
                    />
                );

                key++;
            }

            if (showDownloadCounter) {
                content.push(
                    <Fragment key={key}>
                        <div ref={tooltipRef} className={classNames(styles.counter)}>
                            <DownloadCounter
                                className={classNames(styles.border)}
                                downloads={downloads}
                                downloadLimit={downloadTotal}
                                onClick={handleCounterClick}
                                isNewDataList
                                animate={animateNewTableGrid}
                            />
                        </div>
                    </Fragment>
                );

                key++;
            }
        }

        const bothActive = props.isPublic && isFavoritesEnable && Boolean(isInFavorites);
        const noneActive = !props.isPublic && isFavoritesEnable && !isInFavorites;

        return (
            <div
                className={classNames(styles.bottom_actions, {
                    [styles.animate_bottom_actions]: animateNewTableGrid,
                    [styles.skip_bottom_actions_animation]: bothActive || noneActive,
                    [styles.single_action]: content.length === 1,
                    [styles.limit]: showDownloadCounter,
                })}
            >
                {content}
            </div>
        );
    }, [
        storage,
        denyDoAction,
        progress,
        props.isPublic,
        props.isSeoEnabled,
        isFavoritesEnable,
        isInFavorites,
        onCancelUpload,
        isPublishEnable,
        id,
        showPublicButton,
        onPublish,
        onFavorites,
        showDownloadCounter,
    ]);

    const floatingActions = useMemo(() => {
        if (denyDoAction) {
            return null;
        }

        const content: ReactElement[] = [
            <Checkbox
                key={0}
                className={styles.check}
                animate={animateNewTableGrid}
                viewType={EViewMode.thumbs}
                onCheckboxClick={onCheckboxClick}
                isSelected={isSelected}
                isDisabled={denyDoAction}
            />,
        ];

        if (showDownloadButton && !isPopup && !forceCheckbox && (storage !== EStorageType.public || !isPublicDownloaded)) {
            content.push(
                <Download
                    key={1}
                    onClick={onDownload}
                    ext={isFolder ? 'zip' : ext}
                    size={size}
                    id={id}
                    storage={storage}
                    animate={animateNewTableGrid}
                    viewType={EViewMode.thumbs}
                    className={styles.download}
                    isLarge={false}
                />
            );
        }

        return (
            <div
                className={classNames(styles.floating_actions, {
                    [styles.animate_floating_actions]: animateNewTableGrid,
                })}
            >
                {content}
            </div>
        );
    }, [
        denyDoAction,
        ext,
        forceCheckbox,
        id,
        isFolder,
        isPopup,
        isPublicDownloaded,
        isSelected,
        onCheckboxClick,
        onDownload,
        showDownloadButton,
        size,
        storage,
        webSearchContentEnabled,
    ]);

    const destinationProps = useMemo(() => {
        const destinationAuthor = isAttaches
            ? props.owner
            : {
                  email: isIncomingPublicOrShared ? author ?? '' : '',
                  name: isIncomingPublicOrShared ? ownerName ?? '' : '',
              };

        return {
            size: isAttaches ? SizeType.s : SizeType.l,
            author: destinationAuthor,
            openAttachLetter: isAttaches ? props.openAttachLetter : () => {},
            isAttaches,
        };
    }, [isAttaches, props, isIncomingPublicOrShared, author, ownerName]);

    return (
        <div
            ref={parentItemRef}
            className={classNames(styles.root, className, {
                [styles.selected]: isSelected,
                [styles.active]: isActive,
                [styles.root_animate]: animateNewTableGrid,
            })}
        >
            {floatingActions}
            <Hint text={`${name}${ext ? `.${ext}` : ''}`} showDelay delay={500} positionStrategy={HintPosition.relativeToCursor}>
                <>
                    <div className={styles.picture}>
                        <Pic
                            isFolder={props.isFolder}
                            isPublic={props.isPublic}
                            thumbUrl={props.thumbUrl}
                            kind={props.kind}
                            isVirus={'isVirus' in props && Boolean(props.isVirus)}
                            isAutoDelete={'isAutoDelete' in props && props.isAutoDelete}
                            ext={props.ext || ''}
                            isLoading={denyDoAction}
                            onLoad={handlePictureLoad}
                            hasLoaded={pictureLoaded}
                        />
                        {showDestination && (
                            <Destination
                                className={styles.destination}
                                size={destinationProps.size}
                                author={destinationProps.author}
                                openAttachLetter={destinationProps.openAttachLetter}
                                isAttaches={destinationProps.isAttaches}
                                onAuthorClick={onAuthorContextMenuItem}
                            />
                        )}
                    </div>
                    <div className={styles.details}>
                        <ItemName name={name} ext={ext} id={id} isRenameAvailable={isRenameAvailable} />
                        {/** В условии showWebSearchSource проверяется наличие query в props, поэтому используем !*/}
                        {showWebSearchSource && <WebSearchSource className={styles.source} query={props.query!} />}
                        {showDate && (
                            <Date
                                date={mtime}
                                expires={'publicExpires' in props ? props.publicExpires : undefined}
                                isAutoDelete={'isAutoDelete' in props && props.isAutoDelete}
                            />
                        )}
                        {!showWebSearchSource && !showDate && <Spacing size={14} />}
                    </div>
                </>
            </Hint>
            {!isPopup && bottomActions}
            {showFirstTrackDownloadCountPromo && !isTooltipShown && (
                <TrackDownloadCountPromo targetRef={tooltipRef} downloads={downloads} downloadLimit={downloadTotal} />
            )}
            {showTrackDownloadCountPromo && showTooltipCounter && (
                <TrackDownloadCountPromo
                    targetRef={tooltipRef}
                    downloads={downloads}
                    downloadLimit={downloadTotal}
                    onHide={handleCounterPromoHide}
                />
            )}
            {!isPopup && (
                <div className={styles.pinLeft}>
                    {!!tipData && (
                        <FloatingTooltip
                            target={parentItemRef}
                            size={ETooltipSize.large}
                            text={tipData[0].getTipContent?.(isFolder)}
                            placement={ETooltipPlacement.top}
                            closeOnOutsideClick={false}
                            qaId={tipData[0].id}
                            onClose={tipData[0].onHideClick}
                            usePortal
                        />
                    )}
                </div>
            )}
        </div>
    );
});

DataListItemThumb.displayName = 'DataListItemThumb';
